import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, exhaustMap, map, switchMap, takeUntil, withLatestFrom } from 'rxjs/operators';

import { HomepageSummaryV1Service, SupplierRequestV1Service } from '../../../../api/services';
import { CursorPaginationRequest } from '../../../../api/models/cursor-pagination-request';
import {
  loadFirstPageRequests,
  loadNextPageRequests,
  loadPageRequestsFailure,
  loadPageRequestsSuccess,
  RequestListSource,
} from '../../actions/request/request.actions';
import { selectRequestListState } from '../../selector';
import { selectUserRole, selectUserSupplierV1 } from '../../../../authentication/state/selector';

const PAGE_SIZE = 25;

@Injectable()
export class RequestEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly supplierRequestV1Service: SupplierRequestV1Service,
    private readonly homepageSummaryV1Service: HomepageSummaryV1Service,
    private readonly store: Store
  ) {}

  loadFirstPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadFirstPageRequests),
      withLatestFrom(this.store.select(selectUserRole), this.store.select(selectUserSupplierV1)),
      switchMap(([action, role, supplier]) => {
        const body: CursorPaginationRequest = {
          continuationToken: undefined,
          filters: action.filters,
          sortBy: action.sortBy,
          sortDirection: action.sortDirection,
          size: PAGE_SIZE,
        };
        return this.fetchPage(role, supplier, body, action.source ?? 'all');
      })
    )
  );

  loadNextPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadNextPageRequests),
      withLatestFrom(
        this.store.select(selectUserRole),
        this.store.select(selectUserSupplierV1),
        this.store.select(selectRequestListState)
      ),
      exhaustMap(([, role, supplier, state]) => {
        const body: CursorPaginationRequest = {
          continuationToken: state.continuationToken ?? undefined,
          filters: state.lastFilters,
          sortBy: state.lastSortBy,
          sortDirection: state.lastSortDirection,
          size: PAGE_SIZE,
        };
        return this.fetchPage(role, supplier, body, state.lastSource ?? 'all').pipe(
          takeUntil(this.actions$.pipe(ofType(loadFirstPageRequests)))
        );
      })
    )
  );

  private fetchPage(role: string | null, supplier: any, body: CursorPaginationRequest, source: RequestListSource) {
    return this.resolveListCall(role, supplier, body, source).pipe(
      map((resp) =>
        loadPageRequestsSuccess({
          content: resp.content ?? [],
          continuationToken: resp.continuationToken ?? null,
          hasMore: resp.hasMore === true,
        })
      ),
      catchError((error) => of(loadPageRequestsFailure({ error })))
    );
  }

  private resolveListCall(
    role: string | null,
    supplier: any,
    body: CursorPaginationRequest,
    source: RequestListSource
  ) {
    // Suppliers never see the homepage Action Required view; their scoped endpoint wins.
    if (role === 'supplier' && supplier?.id) {
      return this.supplierRequestV1Service.listSuppliersRequestsV1Cursor({ body });
    }
    if (source === 'myRequests') {
      return this.homepageSummaryV1Service.getMyRequestsV1({ body });
    }
    return this.supplierRequestV1Service.listSupplierRequestsV1Cursor({ body });
  }
}
